import React, { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import ResponsiveImage from '../common/ResponsiveImage';

export default function StorePreview() {
  const navigate = useNavigate();
  const prefersReducedMotion = useReducedMotion();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [storeTitle, setStoreTitle] = useState('Drop Culture Only');
  const [storeSubtext, setStoreSubtext] = useState('Limited drops from the stream.');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const { data: home } = await supabase.from('homepage_content').select('store_preview_title,store_preview_subtext').order('updated_at', { ascending: false }).limit(1);
      if (home?.[0]) {
        if (home[0].store_preview_title) setStoreTitle(home[0].store_preview_title);
        if (home[0].store_preview_subtext) setStoreSubtext(home[0].store_preview_subtext);
      }
      const { data, error } = await supabase.from('store_products').select('*').eq('is_active', true).order('created_at', { ascending: false }).limit(3);
      if (!error && data?.length) {
        setProducts(data.map((item) => ({
          id: item.id,
          name: item.name,
          price: `${item.currency} ${Math.round(item.price_cents / 100)}`,
          image: item.image_url
        })));
      } else {
        setProducts([]);
      }
      setLoading(false);
    };
    load();
  }, []);

  return (
    <section className="bg-[#021206] py-28 px-6 border-t border-white/5">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row items-start lg:items-end justify-between gap-6 mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-white/60 mb-2">Store</p>
            <h2 className="text-5xl lg:text-6xl font-black uppercase">
              {storeTitle}
            </h2>
            <p className="text-white/70 max-w-xl mt-4">
              {storeSubtext}
            </p>
          </div>
          <button
            onClick={() => navigate('/store')}
            className="px-6 py-3 bg-[#A1FF4F] text-black font-bold uppercase tracking-wider"
          >
            Visit Store
          </button>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading && Array.from({ length: 3 }).map((_, idx) => (
            <div
              key={`store-skeleton-${idx}`}
              className="bg-[#0A0E11] border border-white/10 rounded-2xl overflow-hidden animate-pulse"
            >
              <div className="h-64 bg-white/5" />
              <div className="p-6 flex items-center justify-between">
                <div className="space-y-2">
                  <div className="h-4 w-28 bg-white/10 rounded" />
                  <div className="h-3 w-20 bg-white/10 rounded" />
                </div>
                <div className="h-9 w-16 bg-white/10 rounded" />
              </div>
            </div>
          ))}
          {products.map((product) => (
            <motion.div
              key={product.id}
              initial={{ opacity: 0, y: prefersReducedMotion ? 0 : 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: prefersReducedMotion ? 0.2 : 0.4, ease: 'easeOut' }}
              whileHover={prefersReducedMotion ? undefined : { y: -4 }}
              className="bg-[#0A0E11] border border-white/10 rounded-2xl overflow-hidden group transform-gpu will-change-transform"
            >
              <div className="relative h-64 overflow-hidden">
                <ResponsiveImage
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500 ease-out"
                  sizes="(min-width: 1024px) 28vw, (min-width: 640px) 45vw, 92vw"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="p-6 flex items-center justify-between">
                <div>
                  <h3 className="font-bold uppercase">{product.name}</h3>
                  <p className="text-white/60 text-sm">{product.price}</p>
                </div>
                <button
                  onClick={() => navigate(`/store?product=${product.id}`)}
                  className="px-4 py-2 bg-white/5 text-white/80 hover:text-black hover:bg-[#A1FF4F] transition-colors"
                >
                  View
                </button>
              </div>
            </motion.div>
          ))}
        </div>
        {!loading && products.length === 0 && (
          <p className="text-white/60 mt-8">No featured store items yet.</p>
        )}
      </div>
    </section>
  );
}
